import { useMemo, useState, useRef, useEffect } from 'react'
import { useReportExportView } from './reportExportViewContext'
import { useAliasedLabel, usePublishAliasColumns } from './columnAliasContext'
import ControlCheckbox from '@components/common/ControlCheckbox'
import ReportDataGrid from './ReportDataGrid'
import ReportGroupedGrid from './ReportGroupedGrid'
import { useReportName } from './reportRunStateContext'
import useGridControls from './useGridControls'
import { toNumber, groupFlatReport } from './reportGridUtils'
import { detectSectionedSummaryCsv, buildSectionedSummaryReport } from './sectionedCsv'
import { escapeHtml } from './escapeHtml'

/**
 * CC-49304 — routes the TABULAR specialized reports (which come back from cb-api
 * as { headers, rows, meta }) through the universal ReportDataGrid, so they pick
 * up sort / per-column filter / column show-hide / resize / persisted layout just
 * like the generic flat reports.
 *
 * Drop-in replacement for <CsvReportTable> in the in-scope specialized reports
 * (Time Entries, Timekeeper Analysis, Monthly Productivity, Fee Receipts, Billing
 * Realization, Rev & Comm, Fee Allocation, Payments-by-Case, Bill Journal, AP /
 * AP Detail, Reconciliation History, Matter Metrics, Status). Bespoke reports
 * (P&L, Balance Sheet, GL, Trial Balance, Calendar, external embeds) keep their
 * own renderers.
 *
 * The adapter only RESHAPES the server's data into the generic { columns,
 * reportData, reportFooterCells } shape — it never recomputes values. A "Total" /
 * "Grand Total" row is surfaced as ReportDataGrid's server-totals strip (so the
 * filtered-totals coherence + SUM-only recompute from CC-49382 apply here too).
 *
 * Client-side Group By + column-value filter (CC-49596/49346/49349) are layered
 * on the finished report by the shared useGridControls hook — the same toolbar
 * AutoReportGrid uses — so the two adapters can't drift.
 */

// A cell looks numeric (currency, %, parens-negative) — mirrors CsvReportTable.
const numericLike = (s) => /^[\s]*[-(]?\$?\s*[\d,.]+\)?[\s]*%?$/.test(String(s ?? ''))

// CB CSV writers put the "Grand Total" label in different columns per report;
// treat any row whose first non-empty cell starts with "Total"/"Grand Total" as
// the totals row (mirrors CsvReportTable.isTotalRow).
const isTotalRow = (row, headers) => {
  for (const h of headers) {
    const v = String(row[h] ?? '').trim()
    if (!v) continue
    return /^(grand\s+)?total\b/i.test(v)
  }
  return false
}

// ---- CC-49617: legacy grouped-CSV detection + restructure -------------------
// Legacy grouped CSV exports (Time Entries isGrouped, TKA) embed structure in
// the flat row stream: group-header rows (only the first column populated),
// continuation rows (blank group column), and per-group total rows ("Matter
// Total", "Total ..."). Column-sorting that flat stream interleaves them, so we
// detect the two shapes and restructure into REAL groups for ReportGroupedGrid
// (which sorts within each group). Normal flat CSVs must never trip this.

const firstNonEmptyCell = (row, headers) => {
  for (const h of headers) {
    const v = String(row[h] ?? '').trim()
    if (v) return v
  }
  return ''
}

// A group-header row: only the FIRST column carries a value.
const isHeaderOnlyRow = (row, headers) => {
  if (headers.length < 2) return false
  if (String(row[headers[0]] ?? '').trim() === '') return false
  return headers.slice(1).every((h) => String(row[h] ?? '').trim() === '')
}

// An embedded total row: the first non-empty cell reads like a total label
// ("Matter Total", "Total", "Total for Smith", "Grand Total").
const isEmbeddedTotalLabel = (v) => /\btotal\b/i.test(v)
const isGrandTotalLabel = (v) => /^grand\s+total\b/i.test(v)

/**
 * CC-49860: the report's closing total BLOCK in a forward-fill CSV — the row
 * whose FIRST column is total-labelled, plus the continuation rows after it
 * (blank first column). Verified against live report 26, which closes with
 *   { Timekeeper: 'Total', Period: 'PTD', … }
 *   { Timekeeper: '',      Period: 'YTD', … }
 * — the same label-on-first-row shape as every timekeeper block. Looking at the
 * first NON-EMPTY cell instead sees "YTD" on the second row and misses it.
 * Returns { startIndex, rows }; startIndex is -1 when the report has no such block.
 */
const trailingTotalBlock = (rows, headers) => {
  if (!rows?.length || !headers?.length) return { startIndex: -1, rows: [] }
  const firstCol = (r) => String(r[headers[0]] ?? '').trim()
  const block = []
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (firstCol(rows[i]) === '') { block.unshift(rows[i]); continue }
    if (isEmbeddedTotalLabel(firstCol(rows[i]))) {
      block.unshift(rows[i])
      return { startIndex: i, rows: block }
    }
    break
  }
  return { startIndex: -1, rows: [] } // trailing rows, but nothing opening them
}

/**
 * How many rows of that block are still present in `report.reportData`, which is
 * `rows` minus the single `totalRowIdx` entry — so they are its tail and must be
 * sliced off before bucketing, or the grand total renders as a data row.
 */
const trailingInData = (rows, headers, totalRowIdx) => {
  const { startIndex } = trailingTotalBlock(rows, headers)
  if (startIndex < 0) return 0
  return rows.length - startIndex - (totalRowIdx >= startIndex ? 1 : 0)
}

/**
 * Heuristic from CC-49617:
 *  - 'sectioned-summary': section labels PLUS multi-line summary blocks in the
 *    trailing label+amount columns (CC-50050, Production & Origination). Tested
 *    first because such a CSV also satisfies 'header-rows', which mis-reads its
 *    blocks — see sectionedCsv.js;
 *  - 'header-rows': >= 2 rows where only the first header's value is non-empty
 *    AND at least one embedded total row (Time Entries isGrouped shape);
 *  - 'forward-fill': >= 30% of data rows have a blank first column with at
 *    least two distinct block starters (TKA continuation-row shape);
 *  - null: a normal flat CSV — leave it alone.
 */
const detectGroupedCsv = (headers, rows) => {
  if (!headers || headers.length < 2 || !rows || rows.length < 3) return null
  if (detectSectionedSummaryCsv(headers, rows)) return 'sectioned-summary'
  let headerOnly = 0
  let totalLike = 0
  let blankFirst = 0
  const starters = new Set()
  let dataRows = 0
  for (const r of rows) {
    if (isHeaderOnlyRow(r, headers)) { headerOnly += 1; continue }
    if (isEmbeddedTotalLabel(firstNonEmptyCell(r, headers))) { totalLike += 1; continue }
    dataRows += 1
    const first = String(r[headers[0]] ?? '').trim()
    if (first === '') blankFirst += 1
    else starters.add(first)
  }
  if (headerOnly >= 2 && totalLike >= 1) return 'header-rows'
  if (dataRows >= 3 && starters.size >= 2 && blankFirst / dataRows >= 0.3) return 'forward-fill'
  return null
}

// CC-50013 (G3): `pageStateRef` / `initialPageState` are forwarded straight to
// the underlying grid so a page rendering through this adapter can offer saved
// views. Without this pass-through the six CsvReportGrid pages could mount a
// Views menu that saved `pageState: null` and never restored — the CC-49732
// defect, arriving on a different render path.
const CsvReportGrid = ({ data, storageKey, meta: metaProp, defaultSortLabel, pageStateRef, initialPageState }) => {
  // CC-49594: publish the grid's export view to the surrounding shell (if
  // any) so it can offer a client-built PDF.
  const exportCtx = useReportExportView()
  const exportViewRef = useRef(null)
  useEffect(() => {
    if (!exportCtx) return
    exportCtx.ref.current = () => exportViewRef.current?.()
    exportCtx.onPublish?.()
  }, [exportCtx])
  // CC-50291: per-firm column names, resolved by the stable header key.
  const aliasedLabel = useAliasedLabel()
  const allHeaders = data?.headers || []
  // Memoized because `provenance` below takes it as a dependency, and an inline
  // `a || b || []` produces a fresh array identity on every render — the repo's
  // standing rule for `|| []` used as a dep.
  const meta = useMemo(() => data?.meta || metaProp || [], [data?.meta, metaProp])

  // CC-49833: duplicate/blank CSV headers used to collapse into ONE record key
  // server-side, so every sibling column read the same (last) value or blank —
  // the Timekeeper Analysis "only one column renders values" bug. Key columns
  // uniquely: the first occurrence of a non-blank header keeps the raw name;
  // blanks and repeats use the positional col${i} key cb-api now writes.
  // Older cb-api payloads (no positional keys) fall back to the name-keyed
  // value so mixed deploys degrade to the previous behavior, never worse.
  //
  // Spec 193: every value is HTML-escaped HERE, once. This CSV is CB's export of
  // firm text, and every cell, footer and group header below is built from
  // `rows` and rendered as HTML by the grids. Escaping at the source keeps the
  // detection, total and section-matching logic comparing like with like, and
  // changes none of it: numbers, blanks and "Total" labels carry no & < > " '.
  // `rows` is therefore display HTML; `stripHtml` reads the typed text back.
  const { headerKeys, labelByKey, rows } = useMemo(() => {
    const keys = allHeaders.map((h, i) => (h && allHeaders.indexOf(h) === i ? h : `col${i}`))
    const labels = {}
    keys.forEach((k, i) => { labels[k] = allHeaders[i] })
    const norm = (data?.rows || []).map((r) => {
      const o = {}
      keys.forEach((k, i) => { o[k] = escapeHtml(r[k] ?? r[allHeaders[i]] ?? '') })
      return o
    })
    return { headerKeys: keys, labelByKey: labels, rows: norm }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // CC-49338: "ghost" columns — every value empty or zero (e.g. P&L's stray
  // uncategorized column of 0.00s) — clutter exports and grids. Suppress them
  // by default, keep the first column (row labels), and offer an escape hatch.
  const ghostHeaders = useMemo(() => {
    if (rows.length === 0) return new Set()
    const ghosts = new Set()
    for (const h of headerKeys.slice(1)) {
      let allGhost = true
      for (const r of rows) {
        const v = String(r[h] ?? '').trim()
        if (v === '' || v === '-') continue
        const n = toNumber(v)
        if (n === 0) continue
        allGhost = false
        break
      }
      if (allGhost) ghosts.add(h)
    }
    return ghosts
  }, [headerKeys, rows])
  const [showEmptyCols, setShowEmptyCols] = useState(false)
  // `headers` are the unique column KEYS (render labels via labelByKey).
  const headers = useMemo(
    () => (showEmptyCols ? headerKeys : headerKeys.filter((h) => !ghostHeaders.has(h))),
    [headerKeys, ghostHeaders, showEmptyCols]
  )

  // Sample the first 25 non-empty values per column to decide numeric columns.
  const numericCols = useMemo(() => {
    const cols = new Set()
    for (const h of headers) {
      let total = 0
      let numeric = 0
      for (const r of rows.slice(0, 25)) {
        const v = String(r[h] ?? '').trim()
        if (!v) continue
        total += 1
        if (numericLike(v)) numeric += 1
      }
      if (total >= 3 && numeric / total >= 0.7) cols.add(h)
    }
    return cols
  }, [headers, rows])

  const totalRowIdx = useMemo(
    () => rows.findIndex((r) => isTotalRow(r, headers)),
    [headers, rows]
  )

  // Reshape { headers, rows } → the generic CB report shape ReportDataGrid expects.
  const report = useMemo(() => {
    // CC-49345: per-report default sort. Match the requested label against
    // CSV headers (exact first, then substring) and stamp sortDirectionName so
    // ReportDataGrid seeds DataGrid's initial sorting from column metadata.
    const wanted = (defaultSortLabel || '').toLowerCase()
    // Centerbase's OWN label. The default-sort match must run against this and
    // never against the firm's alias: `defaultSortLabel` is a per-report
    // constant written against CB's wording, so matching the alias would mean
    // renaming a column silently changed which column the report sorts by.
    const cbLabelOf = (h) => String(labelByKey[h] ?? h)
    const defaultSortHeader = wanted
      ? headers.find((h) => cbLabelOf(h).toLowerCase() === wanted) ??
        headers.find((h) => cbLabelOf(h).toLowerCase().includes(wanted))
      : undefined

    // CC-50291: the firm's own word for this column, if an admin typed one.
    // `columnId` stays the POSITIONAL index — it is this reshape's internal
    // pairing with `dataCells` below, not an identity anything is stored
    // under. The alias is keyed by `h`, the stable header key.
    const columns = headers.map((h, i) => ({
      columnId: i,
      label: aliasedLabel(h, cbLabelOf(h)),
      rawLabel: cbLabelOf(h),
      visible: true,
      columnAlignment: numericCols.has(h) ? 'RIGHT' : 'LEFT',
      ...(h === defaultSortHeader ? { sortDirectionName: 'ASC' } : {}),
    }))

    const reportData = rows
      .map((r, ri) => ({ r, ri }))
      .filter(({ ri }) => ri !== totalRowIdx)
      .map(({ r }) => ({
        isGroup: false,
        dataCells: headers.map((h, i) => {
          const raw = r[h] ?? ''
          return {
            columnId: i,
            displayValue: String(raw),
            // dataValue only for numeric columns, so sort/SUM stay value-based.
            dataValue: numericCols.has(h) ? toNumber(raw) : undefined,
          }
        }),
      }))

    // While a value filter is active the shared controls hook RE-SUMS these for
    // the filtered subset (SUM-only guardrail; CC-49354 #6) instead of hiding them.
    const totalRow = totalRowIdx >= 0 ? rows[totalRowIdx] : null
    const reportFooterCells = totalRow
      ? headers
          .map((h, i) => ({ columnId: i, displayValue: String(totalRow[h] ?? '') }))
          .filter((c) => c.displayValue.trim() !== '')
      : []

    return { columns, reportData, reportFooterCells }
  }, [headers, rows, numericCols, totalRowIdx, defaultSortLabel, labelByKey, aliasedLabel])

  // CC-50291: tell the surrounding shell which columns this report actually
  // has, so an admin can rename them. A specialized report's columns exist
  // ONLY here — its generic definition carries none — so without this the
  // rename editor would have nothing to list. CB's own labels, never the
  // aliased ones: the editor shows both, side by side.
  const publishAliasColumns = usePublishAliasColumns()
  useEffect(() => {
    publishAliasColumns?.(headers.map((h) => ({ key: h, label: String(labelByKey[h] ?? h) })))
  }, [publishAliasColumns, headers, labelByKey])

  // CC-49617: when the CSV is a legacy grouped export, restructure the flat
  // stream into ReportGroupedGrid's real group shape so column sort happens
  // WITHIN groups instead of interleaving header/continuation/total rows.
  const detected = useMemo(() => detectGroupedCsv(headers, rows), [headers, rows])
  const structuredReport = useMemo(() => {
    if (detected === 'sectioned-summary') {
      // CC-50050: sections nest (attorney → client) and each closes with a
      // multi-line Production / Origination / Total block; the report closes with
      // Production / Origination / Expenses / Grand Total. Ownership comes from the
      // block's own scope label, so nothing is inferred from position.
      return buildSectionedSummaryReport({ headers, rows, columns: report.columns, numericCols })
    }
    if (detected === 'header-rows') {
      // Header-only rows open a group; a following "X Total" row closes it as
      // the group's footer; a trailing "(Grand) Total" row is the report total.
      // The group value is stamped into blank first-column leaf cells (it was
      // implied by position in the CSV) so filtering / re-grouping stay sound.
      const mkCells = (r, fillFirst) => headers.map((h, i) => {
        let raw = String(r[h] ?? '')
        if (i === 0 && raw.trim() === '' && fillFirst) raw = fillFirst
        return {
          columnId: i,
          displayValue: raw,
          dataValue: numericCols.has(h) ? toNumber(r[h] ?? '') : undefined,
        }
      })
      const mkFooter = (r) => headers
        .map((h, i) => ({ columnId: i, displayValue: String(r[h] ?? '') }))
        .filter((c) => c.displayValue.trim() !== '')
      // CC-49946: the CSV can carry MORE THAN ONE grouping level. Time Entries
      // with "Group Entries = Yes" flattens a nested client → matter → entries
      // structure (CenterbaseWeb/ViewModels/AccountingModels.cs builds
      // groupRow.DataRows.Add(matterRow) and emits BOTH a "Matter Total" and a
      // "Client Total" row). The old single-`current` walk could only hold one
      // level, so each matter header silently closed the client group as empty
      // and "Client Total" fell through to be consumed as the grand total —
      // exactly the missing row QA reported.
      //
      // A stack models the real nesting. The one ambiguity is what a header-only
      // row means when a group is already open: CB gives no depth marker, so we
      // use the only reliable signal in the stream — whether the open group has
      // started collecting its OWN leaf rows. A header immediately after another
      // header is a CHILD (client → matter); a header after leaf rows is a
      // SIBLING (the flat shape, unchanged from CC-49617).
      const groups = []
      const topLeaves = []
      const stack = []
      let grandRow = null
      let leafCount = 0
      const inner = () => stack[stack.length - 1] || null
      const openGroup = (name) => {
        stack.push({ isGroup: true, groupHeaderContent: name, dataRows: [], groupFooterCells: [] })
      }
      const close = (footerRow) => {
        const g = stack.pop()
        if (!g) return
        // Server totals pass through verbatim — never recomputed (SUM-only rule).
        if (footerRow) g.groupFooterCells = mkFooter(footerRow)
        const parent = inner()
        if (parent) parent.dataRows.push(g)
        else groups.push(g)
      }
      const closeAll = () => { while (stack.length) close(null) }
      const innerHasOwnLeaves = () => !!inner() && inner().dataRows.some((r) => !r.isGroup)

      for (const r of rows) {
        if (isHeaderOnlyRow(r, headers)) {
          if (innerHasOwnLeaves()) close(null)
          openGroup(String(r[headers[0]] ?? '').trim())
          continue
        }
        const label = firstNonEmptyCell(r, headers)
        if (isEmbeddedTotalLabel(label)) {
          // A total row closes the INNERMOST open group ("Matter Total" closes
          // the matter, the following "Client Total" closes the client).
          if (stack.length > 0 && !isGrandTotalLabel(label)) { close(r); continue }
          closeAll()
          grandRow = r // a total with nothing open = the grand total
          continue
        }
        const leaf = { isGroup: false, dataCells: mkCells(r, inner()?.name ?? inner()?.groupHeaderContent) }
        leafCount += 1
        if (stack.length) inner().dataRows.push(leaf)
        else topLeaves.push(leaf)
      }
      closeAll()
      return {
        columns: report.columns,
        reportData: [...topLeaves, ...groups],
        reportFooterCells: grandRow ? mkFooter(grandRow) : [],
        recordCount: leafCount,
      }
    }
    if (detected === 'forward-fill') {
      // TKA shape: the group value prints only on each block's first row.
      // Forward-fill the first column, drop embedded total rows (bucket
      // subtotals are recomputed SUM-only by groupFlatReport), then bucket.
      // CC-49860: the report's own TRAILING total row ("Total…", "YTD Total")
      // was being dropped with the bucket totals, so the grand-total strip
      // never rendered — keep the LAST total-labeled row as the footer, its
      // server amounts verbatim (never recomputed).
      const firstLeafVal = (row) => {
        for (const c of row.dataCells || []) {
          const v = String(c.displayValue ?? '').trim()
          if (v) return v
        }
        return ''
      }
      const totalRows = []
      let last = ''
      // `trailingInData` (computed below) is how many rows of the closing total
      // block are still present in reportData; they are its tail and must not be
      // rendered as leaves.
      const dataRows = report.reportData || []
      const leaves = dataRows
        .slice(0, dataRows.length - trailingInData(rows, headers, totalRowIdx))
        .filter((row) => {
          if (isEmbeddedTotalLabel(firstLeafVal(row))) {
            totalRows.push(row)
            return false
          }
          return true
        })
        .map((row) => {
          const cell = (row.dataCells || []).find((c) => String(c.columnId) === '0')
          const v = String(cell?.displayValue ?? '').trim()
          if (v) { last = v; return row }
          if (!last) return row
          return {
            ...row,
            dataCells: row.dataCells.map((c) =>
              String(c.columnId) === '0' ? { ...c, displayValue: last } : c
            ),
          }
        })
      const g = groupFlatReport({ ...report, reportData: leaves }, '0')
      // CC-49860: the report's own closing total rows are the grand-total footer.
      // TKA emits TWO of them ("Total PTD" then "Total YTD"), so the footer is the
      // whole TRAILING RUN of total-labeled rows, not a single row.
      //
      // The previous fix could only ever surface one, and not even the right one:
      // `report.reportFooterCells` is built from the FIRST row matching isTotalRow
      // (rows.findIndex), which for TKA is "Total PTD". That value is non-empty, so
      // the `? :` below short-circuited and the last-total-row fallback never ran —
      // "Total YTD" was dropped, exactly what QA reported.
      //
      // Read the trailing run off the ORIGINAL csv rows so ordering is CB's, and
      // keep intermediate per-block totals excluded (groupFlatReport recomputes
      // bucket subtotals SUM-only). Amounts pass through verbatim, never recomputed.
      //
      // The trailing total is a BLOCK, not a run of total-labelled rows. Verified
      // against the live report 26 payload: it closes with
      //     { Timekeeper: 'Total', Period: 'PTD', … 387.86 }
      //     { Timekeeper: '',      Period: 'YTD', … $138,735.46 }
      // i.e. exactly the same label-on-first-row shape as every timekeeper block.
      // Keying on the first NON-EMPTY cell saw "YTD" on the second row, decided it
      // wasn't a total, and stopped — which is why only "Total PTD" rendered. It
      // also left that YTD row in the leaves, so the grand YTD total was being
      // drawn as a data row inside the last timekeeper's group.
      // So: walk back over the continuation rows (blank FIRST column), then
      // require the row that opens the block to be total-labelled.
      const firstCol = (r) => String(r[headers[0]] ?? '').trim()
      const trailing = trailingTotalBlock(rows, headers).rows
      const footerRowCells = (r, idx) => headers
        .map((h, i) => {
          // Forward-fill the block label into the continuation rows, the same way
          // the leaves above are filled — so the footer reads "Total / PTD" then
          // "Total / YTD" instead of leaving the second row unlabelled. Amounts
          // are untouched: server values, verbatim.
          let v = String(r[h] ?? '')
          if (i === 0 && idx > 0 && v.trim() === '' && trailing[0]) v = firstCol(trailing[0])
          return { columnId: i, displayValue: v }
        })
        .filter((c) => c.displayValue.trim() !== '')
      const reportFooterRows = trailing.length > 0
        ? trailing.map(footerRowCells).filter((cells) => cells.length > 0)
        : (report.reportFooterCells || []).length > 0
          ? [report.reportFooterCells]
          : totalRows.length > 0
            ? [(totalRows[totalRows.length - 1].dataCells || [])
                .map((c) => ({ columnId: c.columnId, displayValue: String(c.displayValue ?? '') }))
                .filter((c) => c.displayValue.trim() !== '')]
            : []
      return {
        ...g,
        // First row stays the canonical single-row footer for existing consumers
        // (export view, AI grounding); the grid renders the full set.
        reportFooterCells: reportFooterRows[0] || [],
        reportFooterRows,
        recordCount: leaves.length,
      }
    }
    return report
  }, [detected, report, headers, rows, numericCols])

  // CC-49596/49346/49349: shared Group By + value-filter toolbar (identical to
  // AutoReportGrid's). Grouping reuses groupFlatReport — SUM-only subtotals
  // validated against the server grand-total row — with forward-fill for
  // legacy grouped CSVs that only print the group value on each block's first row.
  const reportName = useReportName()
  const { controls, displayReport, grouped, groupBy } = useGridControls(structuredReport, storageKey)

  // Keep the preamble lines that say something the page does not already say.
  // A line is dropped when, ignoring case, punctuation and a trailing "Report",
  // it matches the report's own name — that is the duplicated title.
  const provenance = useMemo(() => {
    const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').replace(/\breport\b/g, '').trim()
    const own = norm(reportName)
    if (!own) return meta
    return meta.filter((line) => norm(line) !== own)
  }, [meta, reportName])

  // Render the grouped grid whenever the display report actually carries groups
  // — the user's Group By (hook) OR the CC-49617 auto-restructure above.
  const showGrouped = grouped || (displayReport?.reportData || []).some((r) => r.isGroup)

  return (
    <div className="space-y-2">
      {/*
          CB's CSV export preamble, echoed as provenance — every row before the
          real header, first cell only (cb-api parseExportCsv). On most reports
          its first line is just the report's own name, which sat in a bordered
          strip inches under the page's <h1> saying nearly the same words
          ("Timekeeper Analysis" vs "Timekeeper Analysis Report"). QA read that as
          stray floating text, correctly.

          The REST of the preamble is not noise — report 168 returns
          "Matter Metrics Report • January 2026 to August 2026", and that date
          range is what the SERVER actually used. Dropping the whole strip would
          throw away provenance to fix a duplication, so drop only the lines that
          restate the report name.
      */}
      {provenance.length > 0 && (
        <div className="bg-gray-50 border border-gray-100 rounded-lg px-4 py-2 text-xs text-gray-500">
          {provenance.join(' • ')}
        </div>
      )}
      {ghostHeaders.size > 0 && (
        <div data-print-hide className="px-1 text-xs text-gray-600">
          {/* B4 (spec 117): the drawn control. This one is NOT in a popover — it
              is a standalone line above the grid (CC-49338's opt-in for all-zero
              columns) — so it takes the control and not the row treatment. */}
          <label className="inline-flex items-center gap-2.5 cursor-pointer">
            <ControlCheckbox
              checked={showEmptyCols}
              onChange={(e) => setShowEmptyCols(e.target.checked)}
            />
            Show {ghostHeaders.size} empty {ghostHeaders.size === 1 ? 'column' : 'columns'} (all values zero or blank)
          </label>
        </div>
      )}
      {/* `controls` goes INTO the grid card as headerRow (its own row above the
          record count), not above the card as a loose strip — Jordan, 2026-08-26. */}
      {showGrouped ? (
        <ReportGroupedGrid key={`grp:${groupBy}`} report={displayReport} storageKey={storageKey} exportViewRef={exportViewRef} pageStateRef={pageStateRef} initialPageState={initialPageState} headerRow={controls} />
      ) : (
        <ReportDataGrid report={displayReport} storageKey={storageKey} exportViewRef={exportViewRef} pageStateRef={pageStateRef} initialPageState={initialPageState} headerRow={controls} />
      )}
    </div>
  )
}

export default CsvReportGrid
